import { Info, RefreshCcw } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/use-toast";
import { useReprocessDepositsMutation } from "@/services/deposit-reconciliation";
import type { IApiError } from "src/@types/deposit-reconciliation";

import { formatPeriodLabel } from "@/utils/date-range";

interface ReprocessModalProps {
  open: boolean;
  onClose: () => void;
  defaultPeriodStart?: string;
  defaultPeriodEnd?: string;
}

export default function ReprocessModal({
  open,
  onClose,
  defaultPeriodStart,
  defaultPeriodEnd,
}: ReprocessModalProps) {
  const [reprocess, { isLoading }] = useReprocessDepositsMutation();

  const periodLabel: string | null = formatPeriodLabel(
    defaultPeriodStart,
    defaultPeriodEnd,
  );
  const isValid: boolean = !!periodLabel;

  const handleConfirm = async () => {
    if (!defaultPeriodStart || !defaultPeriodEnd) return;
    try {
      await reprocess({
        dateStart: defaultPeriodStart,
        dateEnd: defaultPeriodEnd,
      }).unwrap();
      toast({
        description:
          "Reprocessamento iniciado. Os depósitos vão aparecer conforme forem processados.",
        duration: 6000,
      });
      onClose();
    } catch (err) {
      const error = err as IApiError;
      const status = error?.status;
      const msg =
        status === 403
          ? "Apenas administradores podem reprocessar."
          : status === 400
            ? error?.data?.message ?? "Período inválido."
            : error?.data?.message ?? "Não foi possível iniciar o reprocessamento.";
      toast({
        variant: "destructive",
        description: msg,
        duration: 5000,
      });
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={next => {
        if (!next) onClose();
      }}
    >
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <RefreshCcw className="w-5 h-5 text-[#2B0C1A]" />
            Reprocessar
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                O sistema vai buscar os extratos bancários que ainda
                <strong> não foram processados</strong> no período selecionado.
              </p>

              <div className="flex items-start gap-2 p-2 bg-sky-50 border border-sky-200 rounded text-xs text-sky-900">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Isso pode levar alguns minutos. Os depósitos vão aparecer
                  conforme forem processados.
                </span>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-500">
                  Período
                </span>
                <span className="text-base font-semibold text-[#2B0C1A]">
                  {periodLabel}
                </span>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose} disabled={isLoading}>
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            className="bg-[#2B0C1A] hover:bg-[#4B303C] text-white"
          >
            {isLoading ? "Iniciando…" : "Reprocessar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
